import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowLeft, Shield, MapPin, Phone, Mail } from 'lucide-react';

export const CGVPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <Button 
            onClick={onBack}
            variant="ghost" 
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour au site
          </Button>
          
          <div className="text-center">
            <img 
              src="/assets/logos/zn-logo.png" 
              alt="Z&N Solar Logo" 
              className="w-16 h-16 mx-auto mb-4"
            />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Conditions Générales de Vente
            </h1>
            <p className="text-lg text-gray-600">Z&N Solar - L'excellence sans compromis</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Company Identification */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Shield className="w-5 h-5 text-yellow-600" />
                  <span>1. Identification du Fournisseur</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p><strong>Raison sociale :</strong> Zeïna-Nina SARL</p>
                    <p><strong>Nom commercial :</strong> Z&N SOLAR</p>
                    <p><strong>Forme juridique :</strong> SARL</p>
                    <p><strong>Capital social :</strong> 1 000 000 FCFA</p>
                  </div>
                  <div>
                    <p><strong>Marque détenue par :</strong> M&S HOLDING – Paris, France</p>
                    <p><strong>Solutions de financement :</strong> M&S FINANCE (partenaire exclusif)</p>
                    <p><strong>Directeur de publication :</strong> Seydou Mallé</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delivery & Fees */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>2. Livraison & Frais</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-gray-700">
                  <li>• Les frais de livraison sont entièrement à la charge du client, sauf accord écrit contraire.</li>
                  <li>• Les risques sont transférés au client dès la remise au transporteur ou livraison à l'adresse indiquée.</li>
                  <li>• Les produits restent la propriété de Z&N SOLAR jusqu'au paiement complet.</li>
                </ul>
              </CardContent>
            </Card>

            {/* Warranty */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-yellow-600">3. Garantie Commerciale 10 ans</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <h4 className="font-semibold text-yellow-800 mb-2">Conditions de garantie :</h4>
                  <ul className="space-y-2 text-yellow-700">
                    <li>• <strong>Durée :</strong> Garantie pièces et main d'œuvre pendant 10 ans à compter de la livraison</li>
                    <li>• <strong>Condition :</strong> Installation réalisée par un technicien ou ingénieur agréé par Z&N SOLAR</li>
                    <li>• <strong>Prise en charge :</strong> Frais de retour pris en charge par Z&N SOLAR en cas de panne non liée à une mauvaise utilisation</li>
                    <li>• <strong>Conformité CE :</strong> Tous nos produits PowMr respectent les normes européennes</li>
                    <li>• <strong>Cycles de vie :</strong> Plus de 6000 cycles garantis sur nos batteries lithium</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Disputes */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>4. Gestion des Litiges</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Droit applicable :</strong> Droit malien et Actes Uniformes OHADA</li>
                  <li>• <strong>Réclamation préalable :</strong> Auprès du service client dans un délai de 30 jours</li>
                  <li>• <strong>Médiation :</strong> Possibilité de médiation avant toute procédure judiciaire</li>
                  <li>• <strong>Juridiction compétente :</strong> 
                    <ul className="ml-4 mt-1 space-y-1">
                      <li>- Tribunal malien pour consommateurs</li>
                      <li>- Tribunal de commerce de Bamako pour professionnels</li>
                    </ul>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Payment */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>5. Paiement Échelonné & Financement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-gray-700">
                  <li>• Paiement en plusieurs fois possible selon calendrier défini à la commande</li>
                  <li>• <strong>M&S FINANCE</strong> est le partenaire exclusif pour toutes les solutions de financement proposées par Z&N SOLAR</li>
                  <li>• Conditions claires de retard et suspension de livraison en cas de non-paiement</li>
                </ul>
              </CardContent>
            </Card>

            {/* Additional Clauses */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>6. Clauses Additionnelles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>Force majeure :</strong> Coupures, blocages, cyberattaques, catastrophes naturelles</li>
                  <li>• <strong>Communication électronique :</strong> Acceptée comme preuve contractuelle</li>
                  <li>• <strong>Limitation de garantie :</strong> Liée au stockage et obligation d'entretien</li>
                  <li>• <strong>Propriété intellectuelle :</strong> Protection et confidentialité</li>
                  <li>• <strong>Données personnelles :</strong> Protection conforme RGPD / Mali</li>
                </ul>
              </CardContent>
            </Card>

          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-yellow-600">Contact</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="w-5 h-5 text-yellow-600 mt-1 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Siège social</p>
                    <p className="text-sm text-gray-600">Bamako, Samaya, Côté BMS, Porte 01 – Mali</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Phone className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium">Téléphones</p>
                    <p className="text-sm text-gray-600">Bamako: +223 66 78 33 02</p>
                    <p className="text-sm text-gray-600">Paris: +33 6 23 73 77 80</p>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium">Emails</p>
                    <p className="text-sm text-gray-600">contact@znsolar.fr</p>
                    <p className="text-sm text-gray-600">seydoumalle@znsolar.fr</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Important Notes */}
            <Card className="border-0 shadow-lg bg-gradient-to-r from-yellow-50 to-gray-50">
              <CardHeader>
                <CardTitle>Points Importants</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <Badge className="bg-yellow-100 text-yellow-800 mb-2">
                    Garantie conditionnelle
                  </Badge>
                  <p className="text-gray-700">
                    La garantie est valable uniquement sous condition d'installation par un professionnel agréé Z&N SOLAR.
                  </p>
                  
                  <Badge className="bg-yellow-100 text-yellow-800 mb-2 mt-4">
                    Frais de retour
                  </Badge>
                  <p className="text-gray-700">
                    Pris en charge par Z&N SOLAR sauf en cas de mauvaise utilisation du produit.
                  </p>
                  
                  <Badge className="bg-yellow-100 text-yellow-800 mb-2 mt-4">
                    Financement exclusif
                  </Badge>
                  <p className="text-gray-700">
                    M&S FINANCE est le partenaire exclusif pour toutes les solutions de financement.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>Document mis à jour - Z&N Solar © {new Date().getFullYear()}</p>
          <p className="mt-2">Pour toute question concernant ces CGV, contactez notre service client.</p>
        </div>
      </div>
    </div>
  );
};